import { Account } from 'common-entities';
import {
  CKForm,
  Input,
  IsInvertedProvider,
  Layout,
  Panel,
  PropTypes,
  RadioGroup,
  SingleSelect,
  createComponent,
  enums,
} from 'console-kit';
import React from 'react';
import { connect } from 'react-redux';
import { EmailNotificationsWarnings, emailDomainUtils } from '../../../../../../common/components/EmailNotifications';
import { EMAIL_INTEGRATIONS_FORM_ID } from './email-designer';

export const FRIENDLY_CHAR_LIMIT = 50;

const FromAddress = CKForm.validatable({
  isRequired: CKForm.Validators.isRequired(),
})(Input);

const FromDomain = CKForm.validatable({
  isRequired: CKForm.Validators.isRequired(),
})(SingleSelect);

const DesignerSettingsTab = createComponent({
  displayName: 'designerSettingsTab',
  propTypes:   {
    id:                       PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldChanged:             PropTypes.func.isRequired,
    from:                     PropTypes.string,
    to:                       PropTypes.string,
    replyTo:                  PropTypes.string,
    subject:                  PropTypes.string,
    type:                     PropTypes.oneOf(['text', 'html']),
    name:                     PropTypes.string,
    checkEmailTemplateTokens: PropTypes.func.isRequired,
    //CONSTS
    //these 2 are for internal use ONLY, they will merge and form the 'from' property
    _fromDomain:              PropTypes.string,
    _fromAddress:             PropTypes.string,
    _fromFriendly:            PropTypes.string,
    _replyToFriendly:         PropTypes.string,
    _replyTo:                 PropTypes.string,
  },
  defaultProps: {
    from:             '',
    //these 2 are for internal use ONLY, they will merge and form the 'from' property
    _fromDomain:      '',
    _fromAddress:     '',
    to:               '',
    replyTo:          '',
    subject:          '',
    type:             'html',
    name:             '',
    _fromFriendly:    '',
    _replyToFriendly: '',
    _replyTo:         '',
  },
  handlers: {
    nameChanged:
      ({ fieldChanged }) =>
        (e, { value }) => {
          fieldChanged({
            name: value || '',
          });
        },

    instancePropChanged:
      ({ fieldChanged }) =>
        (e, { value }) => {
          fieldChanged({
            instanceConfig: {
              [e.target.name]: value,
            },
          });
        },

    friendlyPropChanged:
      ({ fieldChanged }) =>
        (e, { value }) => {
          if (value.length <= FRIENDLY_CHAR_LIMIT) {
            fieldChanged({
              instanceConfig: {
                [e.target.name]: value,
              },
            });
          }
        },

    fromDomainEmailChanged:
      ({ fieldChanged }) =>
        (e, { value }) => {
          fieldChanged({
            instanceConfig: {
              _fromDomain: value || ' ',
            },
          });
        },

    typeChanged:
      ({ fieldChanged }) =>
        (e, { value }) => {
          fieldChanged({
            instanceConfig: {
              type: value,
            },
          });
        },
  },
  lifecycles: {
    shouldComponentUpdate(nextProps) {
      return !_.isEqual(this.props, nextProps);
    },
  },

  render(props) {
    const domainOptions = emailDomainUtils.getFilteredDomains(props.accountEmailDomains);

    return (
      <IsInvertedProvider isInverted={true}>
        <div className="campaign-customise-settings-tab">
          <Panel
            header="Settings"
            byline="Enter an internal name for your email so that you can identify it later."
            hasBorder={true}
          >
            <Input value={props.name} label="Name" type="text" onChange={props.nameChanged} />
          </Panel>

          <Panel
            header="Email Header"
            byline="Set the header fields for your email, including the subject, who the email will be from, and who replies should be sent to."
            hasBorder={false}
          >
            <Layout>
              <Layout.Row>
                <Layout.Column>
                  <Input
                    label="Subject"
                    type="text"
                    name="subject"
                    value={props.subject}
                    onChange={props.instancePropChanged}
                    onBlur={props.checkEmailTemplateTokens}
                  />
                </Layout.Column>
              </Layout.Row>

              <Layout.Row
                columnStyles={{
                  verticalAlign: enums.positions.alignColumns.BOTTOM,
                }}
              >
                <Layout.Column columnWidth={enums.widths.FIVE}>
                  <FromAddress
                    formId={EMAIL_INTEGRATIONS_FORM_ID}
                    fieldId="From address"
                    label="From"
                    type="text"
                    name="_fromAddress"
                    value={props._fromAddress}
                    onChange={props.instancePropChanged}
                    onBlur={props.checkEmailTemplateTokens}
                  />
                </Layout.Column>

                <Layout.Column columnWidth={enums.widths.EIGHT}>
                  <FromDomain
                    formId={EMAIL_INTEGRATIONS_FORM_ID}
                    fieldId="Domain"
                    label=""
                    options={domainOptions}
                    placeholder="Select a domain..."
                    value={emailDomainUtils.getAvailableSelectedDomain(domainOptions, props._fromDomain)}
                    search={false}
                    isFluid={true}
                    onChange={props.fromDomainEmailChanged}
                  />
                </Layout.Column>
              </Layout.Row>

              <EmailNotificationsWarnings domainName={props._fromDomain} domainOptions={domainOptions} />

              <Layout.Row>
                <Layout.Column columnWidth={enums.widths.EIGHT}>
                  <Input
                    label="From Friendly Name"
                    type="text"
                    name="_fromFriendly"
                    value={props._fromFriendly}
                    onChange={props.friendlyPropChanged}
                    maxChars={{
                      content:  FRIENDLY_CHAR_LIMIT,
                      position: enums.positions.RIGHT,
                    }}
                  />
                </Layout.Column>
              </Layout.Row>

              <Layout.Row>
                <Layout.Column columnWidth={enums.widths.EIGHT}>
                  <Input
                    label="To"
                    type="text"
                    name="to"
                    value={props.to}
                    onChange={props.instancePropChanged}
                    onBlur={props.checkEmailTemplateTokens}
                  />
                </Layout.Column>
              </Layout.Row>

              <Layout.Row>
                <Layout.Column columnWidth={enums.widths.EIGHT}>
                  <Input
                    label="Reply To"
                    type="text"
                    name="_replyTo"
                    value={props._replyTo}
                    onChange={props.instancePropChanged}
                    onBlur={props.checkEmailTemplateTokens}
                  />
                </Layout.Column>
              </Layout.Row>

              <Layout.Row>
                <Layout.Column columnWidth={enums.widths.EIGHT}>
                  <Input
                    label="Reply To Friendly Name"
                    type="text"
                    name="_replyToFriendly"
                    value={props._replyToFriendly}
                    onChange={props.friendlyPropChanged}
                    maxChars={{
                      content:  FRIENDLY_CHAR_LIMIT,
                      position: enums.positions.RIGHT,
                    }}
                  />
                </Layout.Column>
              </Layout.Row>

              <Layout.Row>
                <Layout.Column>
                  <RadioGroup
                    label="Email format"
                    options={[
                      { label: 'HTML', value: 'html' },
                      { label: 'Text', value: 'text' },
                    ]}
                    onChange={props.typeChanged}
                    selectedValue={props.type}
                    isDisabled={false}
                  />
                </Layout.Column>
              </Layout.Row>
            </Layout>
          </Panel>
        </div>
      </IsInvertedProvider>
    );
  },
});

export default connect((state) => ({
  accountEmailDomains: Account.Selectors.activeAccount(state).emailDomains,
}))(DesignerSettingsTab);
